// src/lib/calc.js
// HomeIQa wholesaling calculator — formulas extracted from the
// "Group of Comping Calculator & AI Prompt" workbook in /resources.

// Repair-cost tiers ($/sqft)
export const REPAIR_TIERS = {
  fullGut:    { label: 'Full Gut',    rate: 70 },
  heavy:      { label: 'Heavy',       rate: 40 },
  moderate:   { label: 'Moderate',    rate: 25 },
  light:      { label: 'Light',       rate: 15 },
  superLight: { label: 'Super Light', rate: 5  }
};

export const DEFAULT_LOCATION_PCT = 70; // classic 70% rule

// Repair Cost = SqFt × Repair Per Sq Ft
export const repairCost = ({ sqft = 0, repairPerSqft = 0 }) =>
  Math.max(0, Number(sqft) * Number(repairPerSqft));

// Cash MAO = (ARV × LocationPct/100) − RepairCost − AssignmentFee
export const cashMAO = ({ arv = 0, locationPct = DEFAULT_LOCATION_PCT, repairs = 0, assignmentFee = 0 }) =>
  Number(arv) * (Number(locationPct) / 100) - Number(repairs) - Number(assignmentFee);

// Novation = (NovationListPrice × 0.9) − AssignmentFee
export const novation = ({ novationListPrice = 0, assignmentFee = 0 }) =>
  Number(novationListPrice) * 0.9 - Number(assignmentFee);

// Pending market analysis: above 40% is preferred (healthy market)
export const percentPending = ({ activePlusPending = 0, activeOnly = 0 }) => {
  const total = Number(activePlusPending);
  if (!total) return 0;
  return ((total - Number(activeOnly)) / total) * 100;
};

// Single-call helper used by the LeadDetail Calculator panel
export function runWholesaleCalc(input) {
  const repairs = repairCost({ sqft: input.sqft, repairPerSqft: input.repairPerSqft });
  const mao = cashMAO({
    arv: input.arv,
    locationPct: input.locationPct,
    repairs,
    assignmentFee: input.assignmentFee
  });
  const nov = novation({
    novationListPrice: input.novationListPrice,
    assignmentFee: input.assignmentFee
  });
  const pending = percentPending({
    activePlusPending: input.activePlusPending,
    activeOnly: input.activeOnly
  });
  return {
    repairs,
    mao,
    novation: nov,
    pendingPct: pending,
    healthyMarket: pending >= 40
  };
}

export const fmtMoney = (n) =>
  isFinite(n) ? `$${Math.round(n).toLocaleString()}` : '—';

// Suggested ARV from a list of comps. Per the training transcript:
// "ARV is just the average of the 3 renovated properties." We average up
// to the top 3 nearest/strongest comps' sale prices. Manual comps the
// agent entered count equally.
export function computeAutoArv(rentcastComps = [], manualComps = []) {
  const all = [
    ...(Array.isArray(rentcastComps) ? rentcastComps : []),
    ...(Array.isArray(manualComps) ? manualComps : [])
  ]
    .map(c => Number(c.lastSalePrice ?? c.salePrice ?? 0))
    .filter(p => p > 0)
    .sort((a, b) => b - a); // highest first

  if (!all.length) return null;
  // Use top 3 if we have at least 3, otherwise average all available.
  const top = all.slice(0, Math.min(3, all.length));
  const avg = top.reduce((a, b) => a + b, 0) / top.length;
  return Math.round(avg);
}
